'use client'

import React from 'react'
import { useMemo } from 'react'
import type { Article, Guide, VisaType, Persona, Media, Video, Event } from '@/payload-types'
import { useSearch } from '../../hooks/useSearch'
import { ResourcesHero } from '../ResourcesHero'
import { ResourcesVisaGuides } from '../ResourcesVisaGuides'
import { ResourcesArticles } from '../ResourcesArticles'
import { ResourcesVideos } from '../ResourcesVideos'
import { ResourcesEvents } from '../ResourcesEvents'
import { ToolsCalculators } from '../ToolsCalculators'

interface ArticleWithPreview extends Article {
  preview: string
}

interface ResourcesContentProps {
  heroHeading: any
  heroDetail?: any
  heroBackgroundImage?: Media
  searchPlaceholder?: string
  visaTypes: VisaType[]
  personas: Persona[]
  guides: Guide[]
  articles: ArticleWithPreview[]
  videos: Video[]
  events: Event[]
}

export function ResourcesContent({
  heroHeading,
  heroDetail,
  heroBackgroundImage,
  searchPlaceholder,
  visaTypes,
  personas,
  guides,
  articles,
  videos,
  events,
}: ResourcesContentProps) {
  const { query, setQuery, results, loading } = useSearch()

  // Filter articles and guides based on cross-collection search results
  const filteredArticles = useMemo(() => {
    if (results === null) return articles
    const ids = new Set(results.filter((r) => r.collection === 'articles').map((r) => r.id))
    return articles.filter((a) => ids.has(a.id))
  }, [articles, results])

  const filteredGuides = useMemo(() => {
    if (results === null) return guides
    const ids = new Set(results.filter((r) => r.collection === 'guides').map((r) => r.id))
    return guides.filter((g) => ids.has(g.id))
  }, [guides, results])

  const isSearching = query.trim().length > 0
  const noResults = isSearching && filteredArticles.length === 0 && filteredGuides.length === 0

  return (
    <>
      <ResourcesHero
        heroHeading={heroHeading}
        heroDetail={heroDetail}
        heroBackgroundImage={heroBackgroundImage}
        searchPlaceholder={searchPlaceholder}
        visaTypes={visaTypes}
        personas={personas}
        searchQuery={query}
        onSearchChange={setQuery}
        searchLoading={loading}
      />

      <div className="bg-stone">
        {noResults && !loading ? (
          <div className="text-center py-20 px-6">
            <p className="text-gray-600 text-lg mb-2">No results found for &ldquo;{query}&rdquo;</p>
            <button
              onClick={() => setQuery('')}
              className="text-brand-blue hover:underline font-medium"
            >
              Clear search
            </button>
          </div>
        ) : (
          <>
            {!isSearching && <ToolsCalculators />}
            {filteredGuides.length > 0 && <ResourcesVisaGuides guides={filteredGuides} />}
            {/* Videos aren't in the search index, so the section steps aside while a
                search is active rather than showing results that ignore the query. */}
            {!isSearching && <ResourcesVideos videos={videos} />}
            {/* Events aren't in the search index either, so the section steps aside
                during a search for the same reason videos do. */}
            {!isSearching && <ResourcesEvents events={events} />}
            {filteredArticles.length > 0 && <ResourcesArticles articles={filteredArticles} />}
          </>
        )}
      </div>
    </>
  )
}
